"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, Layers, Compass, SlidersHorizontal, ArrowRight, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const features = [{
  title: "Moisture barrier engineering",
  description: "Sub-slab vapor barriers and perimeter drainage designed for North Atlanta soil.",
  icon: Layers
}, {
  title: "Egress & code compliance",
  description: "Full ceiling heights, permitted walkout entries, and certified safety egress.",
  icon: Compass
}, {
  title: "Zoned climate control",
  description: "Independent multi-zone heating and dehumidification for year-round comfort.",
  icon: SlidersHorizontal
}, {
  title: "Custom architectural millwork",
  description: "Integrated white oak cabinetry, stone counters, and bespoke acoustic paneling.",
  icon: Sparkles
}];
export default function Basement() {
  const shouldReduceMotion = useReducedMotion();
  const revealMotion = {
    initial: shouldReduceMotion ? {
      opacity: 1,
      y: 0
    } : {
      opacity: 0,
      y: 15
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      amount: 0.2
    },
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  };
  return <section id="basement-remodeling" data-nav="light" className="relative overflow-hidden bg-background py-20 md:py-28 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-16">
          {/* Left Column: Authoritative Editorial Narrative (5 cols) */}
          <motion.div className="lg:col-span-5 flex flex-col justify-center" initial={revealMotion.initial} whileInView={revealMotion.whileInView} viewport={revealMotion.viewport} transition={revealMotion.transition}>
            {/* Craftsmanship Badge */}
            <div className="mb-4 flex items-center">
              <Badge variant="outline" className="rounded-full border-primary/40 bg-primary/10 px-3.5 py-1 text-xs font-semibold tracking-wider uppercase text-foreground">
                <ShieldCheck className="mr-1.5 size-3.5 text-primary" />
                Terrace Level Specialists
              </Badge>
            </div>

            {/* Editorial Heading */}
            <h2 className="font-serif text-3xl font-semibold tracking-tight text-foreground sm:text-4xl lg:text-5xl leading-tight">
              Basement transformations for modern living
            </h2>

            {/* Brushed Gold Hairline Divider */}
            <div className="my-6 h-px w-24 bg-primary/60" />

            {/* Grounded narrative overview */}
            <p className="text-base leading-relaxed text-muted-foreground sm:text-lg">
              Convert your daylight or walkout terrace level into an expansive space for entertaining, hosting guests, and everyday retreat. We design and build seamless lower levels featuring custom wet bars with stone waterfall counters, private guest suites, home media theaters with acoustic dampening, and light-filled personal fitness studios tailored to your home.
            </p>

            {/* 2x2 Feature Grid */}
            <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2">
              {features.map((feature, i) => {
              const IconComponent = feature.icon;
              return <div key={feature.title} data-index={i} className="flex flex-col space-y-2 rounded-xl border border-border/70 bg-card p-4 transition-[border-color,box-shadow] duration-200 hover:border-primary/50 hover:shadow-xs">
                    <div className="flex size-8 items-center justify-center rounded-lg bg-primary/15 text-primary">
                      <IconComponent className="size-4" />
                    </div>
                    <h3 className="text-sm font-semibold text-foreground">
                      {feature.title}
                    </h3>
                    <p className="text-xs leading-normal text-muted-foreground">
                      {feature.description}
                    </p>
                  </div>;
            })}
            </div>

            {/* Primary Action Button */}
            <div className="mt-10 pt-2">
              <Button asChild size="lg" className="rounded-lg bg-primary px-7 py-3.5 font-semibold text-primary-foreground transition-[background-color,box-shadow] hover:bg-primary/90 hover:shadow-md">
                <Link to="/ContactRequestAnEstimatePage">
                  Schedule a design consultation
                  <ArrowRight className="ml-2 size-4" />
                </Link>
              </Button>
            </div>
          </motion.div>

          {/* Right Column: Architectural Photography & Atelier Glass Card (7 cols) */}
          <motion.div className="relative lg:col-span-7" initial={revealMotion.initial} whileInView={revealMotion.whileInView} viewport={revealMotion.viewport} transition={{
          ...revealMotion.transition,
          delay: shouldReduceMotion ? 0 : 0.15
        }}>
            <div className="group relative overflow-hidden rounded-2xl border border-border/80 bg-card shadow-xl ring-1 ring-black/5">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_luxury_basement_remodel.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_luxury_basement_remodel.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_luxury_basement_remodel.png-wvc-sizes" alt="Finished luxury daylight basement with white oak custom cabinetry, cove lighting, and stone wet bar in Brookhaven" loading="lazy" decoding="async" className="aspect-4/3 w-full object-cover object-center transition-[transform] duration-500 ease-out group-hover:scale-102" />

              {/* Subtle perimeter vignette gradient */}
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/40 via-black/10 to-transparent" />
            </div>

            {/* Atelier Frosted Glass Card Overlay */}
            <motion.div whileHover={shouldReduceMotion ? undefined : {
            y: -4
          }} transition={{
            duration: 0.25,
            ease: "easeOut"
          }} className="mt-4 sm:absolute sm:bottom-6 sm:left-6 sm:mt-0 sm:max-w-xs rounded-2xl border border-white/80 bg-card/90 p-5 shadow-lg backdrop-blur-md transition-[border-color,box-shadow] duration-300 hover:border-primary/50 hover:shadow-xl">
              <div className="flex items-center space-x-2 text-primary">
                <ShieldCheck className="size-5 shrink-0" />
                <span className="text-xs font-semibold tracking-wider uppercase text-foreground">
                  Local Jurisdiction Experience
                </span>
              </div>
              <p className="mt-2 text-sm font-semibold text-foreground">
                Daylight & Walkout Terrace Specialists
              </p>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                Fully compliant with Brookhaven, Buckhead, and Dunwoody residential building codes and permitting standards.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>;
}